/**
 * Formatting utilities for DataTable cell renderers
 *
 * This module provides reusable formatting functions for common data types
 * like dates, times, currencies, and other domain-specific formats.
 */

/**
 * Format a date string as MM/DD/YYYY
 */
export const formatDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Format a number as currency with proper locale formatting
 */
export const formatCurrency = (
  amount: number | undefined | null,
  currency: string = 'USD',
  locale: string = 'en-US'
): string => {
  if (amount === undefined || amount === null) return '';

  return amount.toLocaleString(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format a number as a simple currency string (e.g., "$1,234.56")
 */
export const formatPrice = (price: number | undefined | null): string => {
  if (price === undefined || price === null) return '';
  return `$${price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Convert IANA timezone name to abbreviation
 * @param ianaTimezone - IANA timezone name (e.g., "America/New_York")
 * @param date - Date to use for determining timezone offset
 * @returns Timezone abbreviation (e.g., "EST", "EDT", "AZ-MST")
 */
export const getTimezoneAbbreviation = (ianaTimezone: string, date: Date): string => {
  // Special case for Arizona timezone
  if (ianaTimezone === 'US/Arizona' || ianaTimezone === 'America/Phoenix') {
    return 'AZ-MST';
  }

  try {
    // Use Intl.DateTimeFormat to get timezone abbreviation (EDT, CDT, etc.)
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone,
      timeZoneName: 'short'
    });

    const parts = formatter.formatToParts(date);
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart ? tzPart.value : '';
  } catch (e) {
    // Invalid timezone name, show the bad timezone value
    console.warn(`Invalid timezone: ${ianaTimezone}`);
    return `BADTZ: ${ianaTimezone}`;
  }
};

/**
 * Format date and time in a specific timezone
 * Returns formatted values and an error flag if timezone is invalid
 */
export interface FormattedDateTime {
  month: string;
  day: string;
  year: string;
  hour: string;
  minute: string;
  dayPeriod: string;
  error: boolean;
}

export const formatInTimezone = (date: Date, timezone: string): FormattedDateTime => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const parts = formatter.formatToParts(date);
    const getValue = (type: string) => parts.find(p => p.type === type)?.value || '';

    return {
      month: getValue('month'),
      day: getValue('day'),
      year: getValue('year'),
      hour: getValue('hour'),
      minute: getValue('minute'),
      dayPeriod: getValue('dayPeriod'),
      error: false,
    };
  } catch (e) {
    // Invalid timezone - fall back to browser's local timezone

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return {
      month,
      day: day.toString(),
      year: year.toString(),
      hour: hours.toString(),
      minute: minutes.toString().padStart(2, '0'),
      dayPeriod: ampm,
      error: true,
    };
  }
};

/**
 * Format a time string to 12-hour format with AM/PM
 * If timezone is provided, converts to that timezone; otherwise uses local time
 */
export const formatTime = (timeStr: string | undefined | null, timezone?: string): string => {
  if (!timeStr) return 'N/A';
  const date = new Date(timeStr);
  if (isNaN(date.getTime())) return 'N/A';
  try {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...(timezone ? { timeZone: timezone } : {}),
    });
  } catch {
    // Fallback if timezone is invalid
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }
};

/**
 * Measure the width of text in pixels
 * Useful for dynamically sizing tooltips and other UI elements
 */
export const measureTextWidth = (
  text: string,
  font: string = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 250; // fallback

  context.font = font;
  const metrics = context.measureText(text);
  return Math.ceil(metrics.width);
};

/**
 * Truncate text to a maximum length with ellipsis
 */
export const truncateText = (text: string | undefined | null, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Split a pipe-delimited string into an array of trimmed values
 */
export const splitPipeDelimited = (value: string | undefined | null): string[] => {
  if (!value) return [];
  return value
    .split('|')
    .map(v => v.trim())
    .filter(v => v.length > 0);
};
